import { observer } from "mobx-react";
import { FC, useEffect, useRef, useState } from "react";
import { CustomDataGrid } from "src/Shared/Components";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { columns } from "./CompanyGrid.column";
import { LinearProgress, Typography } from "@mui/material";
import CompanyGridToolbar from "../CompanyGridToolbar/CompanyGridToolbar";
import {
  GridFooterContainer,
  GridPagination,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { ICompanyFilter, ICompanyList } from "../../Interfaces";
import { IPageRequest, ISelectOption, PageSort } from "src/Shared/Interfaces";
import { fields } from "./fields";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateEffect } from "src/Shared/Hooks";
import { CompanyGridScope } from "../../Enums/CompanyGridScope.enum";
import { rowCountText } from "../../Tools/GridSortUtils";
import { decideRowFocus } from "./CompanyGrid.focus";

const FLASH_DURATION_MS = 2500;

type Props = {
  scope?: CompanyGridScope;
};

const CompanyGrid: FC<Props> = ({ scope = CompanyGridScope.Home }) => {
  const {
    rootStore: {
      companyStore: {
        gridLoading,
        companyList,
        getCompanies,
        intilizeFilterForm,
        companyFilterForm,
        companyFilterCollection,
        setCompanyFilterCollection,
        setPagination,
        pagination,
        resetHomeGridState,
      },
    },
  } = useStore();

  const [request, setRequest] = useState<IPageRequest<ICompanyFilter>>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [flashRowId, setFlashRowId] = useState<string | null>(null);
  const focusFilterTriedRef = useRef<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Read out of the query string rather than inside the effect below, so the effect can depend on it:
  // selecting a search result while already on Home changes only the query string, and nothing else
  // the effect watches moves (bug 5821).
  const focusRowId = searchParams.get("focus");

  const toSortCollection = (model: GridSortModel): PageSort[] =>
    model
      .filter((x) => !!x.sort)
      .map((x) => ({ propertyName: x.field, isAscending: x.sort === "asc" }));

  useEffect(() => {
    intilizeFilterForm(fields());
    getCompanies({
      filterCollection: { ...companyFilterCollection, gridScope: scope },
      sortCollection: [],
      pageNumber: pagination.page + 1,
      pageSize: pagination.pageSize,
    });
  }, []);

  useUpdateEffect(() => {
    onGridPaginationChange({
      page: 0,
      pageSize: 25,
    });
  }, [companyFilterCollection]);

  const applyFilter = () => {
    setCompanyFilterCollection(getFilterCollection());
  };

  const getFilterCollection = () => {
    const filterColletion = (companyFilterForm?.values() || {
      companyName: "",
      modifiedBy: { label: "", value: "" },
      franchiseID: [],
      franchiseOfficeID: [],
      totalStaffNumber: null,
      vendorTypeID: null,
      leadTypeIDs: [],
      services: [],
      dealStage: [],
      isSLASigned: null,
      slaExpiryDate: null,
      tddaPrimaryRelationshipManager: [],
      healthTickPrimaryRelationshipManager: [],
      createdBy: [],
      primaryContactFullName: null,
      status: null,
    }) as ICompanyFilter;
    filterColletion.totalStaffNumber = filterColletion.totalStaffNumber
      ? filterColletion.totalStaffNumber
      : null;
    filterColletion.isSLASigned = filterColletion.isSLASigned
      ? filterColletion.isSLASigned
      : null;
    filterColletion.slaExpiryDate = filterColletion.slaExpiryDate
      ? filterColletion.slaExpiryDate
      : null;
    filterColletion.companyName = parseCompanyName(filterColletion.companyName);
    filterColletion.gridScope = scope;

    return filterColletion;
  };
  const parseCompanyName = (companyName?: ISelectOption) => {
    if (companyName) {
      companyName.value = companyName.value ? companyName.value : 0;
      return companyName;
    }
    return null;
  };

  useEffect(() => {
    if (request) getCompanies(request);
  }, [request]);

  const onGridPaginationChange = (data: GridPaginationModel) => {
    setPagination({
      ...data,
    });
  };

  // Header clicks sort server-side (ascending first click, blanks last — P.8).
  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    onGridPaginationChange({ page: 0, pageSize: pagination.pageSize });
  };

  useUpdateEffect(() => {
    setRequest({
      filterCollection: { ...companyFilterCollection, gridScope: scope },
      sortCollection: toSortCollection(sortModel),
      pageNumber: pagination.page + 1,
      pageSize: pagination.pageSize,
    });
  }, [pagination]);

  const navigate = useNavigate();

  const onOpen = (params: GridRowParams) => {
    navigate(`/company/${params.id}/overview`);
  };

  // Each focus request gets its own single recovery attempt. Without this the ref stays true for the
  // life of the component, so the second search selection in a session would be refused as "already
  // recovered" and do nothing (bug 5821).
  useEffect(() => {
    focusFilterTriedRef.current = false;
  }, [focusRowId]);

  // A focus query parameter scrolls the row to centre and flashes it (P.8). The branch is decided
  // by decideRowFocus so it can be tested; see CompanyGrid.focus.ts for why the row is often not on
  // the page Home has loaded, which is bug 5722.
  useEffect(() => {
    const action = decideRowFocus({
      focusRowID: focusRowId,
      loading: gridLoading,
      loadedRowIDs: companyList.results?.map((x) => x.companyRowID),
      handedOverName: (location.state as any)?.companyName,
      recoveryAttempted: focusFilterTriedRef.current,
    });

    if (action.kind === "idle") return;

    if (action.kind === "flash") {
      setFlashRowId(action.rowID);
      // Only the focus parameter is consumed; anything else on the query string is not ours to drop.
      const remaining = new URLSearchParams(searchParams);
      remaining.delete("focus");
      setSearchParams(remaining, { replace: true });
      setTimeout(() => {
        document
          .querySelector(`[data-id="${action.rowID}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 100);
      setTimeout(() => setFlashRowId(null), FLASH_DURATION_MS);
      return;
    }

    // Both remaining branches are the one recovery attempt, so the flag is set either way.
    focusFilterTriedRef.current = true;

    if (action.kind === "filterByName" && companyFilterForm) {
      companyFilterForm
        .$("companyName")
        .set("value", { label: action.companyName, value: 0 });
      applyFilter();
      return;
    }

    // Nothing to narrow by, so widen instead: clear Home's filter and go back to page one under the
    // default Updated On descending sort, which is where a just-promoted client sits.
    resetHomeGridState();
    intilizeFilterForm(fields());
  }, [focusRowId, companyList, gridLoading]);

  /**
   * P.8 asks the footer for a row count, singular or plural — the MUI default showed its pagination
   * range instead ("1-25 of 151"), which is not the same thing (bug 5714). Home is paginated
   * server-side over the whole client list, so the controls stay and the count sits beside them;
   * the count is the total from the server, not the rows on this page.
   */
  const Footer = () => (
    <GridFooterContainer>
      <Typography variant="body2" sx={{ pl: 2 }}>
        {rowCountText(companyList.totalNumberOfRecords || 0, "client")}
      </Typography>
      <GridPagination />
    </GridFooterContainer>
  );

  return (
    <>
      <CustomDataGrid sx={{
        m: 1,
        "& .row-focus-flash": {
          animation: `rowFocusFlash ${FLASH_DURATION_MS}ms ease-out`,
        },
        "@keyframes rowFocusFlash": {
          "0%": { backgroundColor: "#ffe0b2" },
          "70%": { backgroundColor: "#ffe0b2" },
          "100%": { backgroundColor: "transparent" },
        },
      }}
        loading={gridLoading}
        columns={columns}
        rowSelection={false}
        rows={companyList.results || []}
        rowCount={companyList.totalNumberOfRecords || 0}
        disableColumnSelector={false}
        style={{
          height: "calc(100vh - 95px)",
        }}
        paginationModel={pagination}
        onPaginationModelChange={(model, _) => onGridPaginationChange(model)}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        getRowClassName={(params) =>
          params.id === flashRowId ? "row-focus-flash" : ""
        }
        slots={{
          toolbar: CompanyGridToolbar,
          loadingOverlay: LinearProgress,
          footer: Footer,
        }}
        getRowId={(row: ICompanyList) => row.companyRowID}
        onRowDoubleClick={onOpen}
        slotProps={{
          toolbar: {
            onApplyFilter: () => applyFilter(),
          },
        }}
      />
    </>
  );
};

export default observer(CompanyGrid);
